import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTable, usePagination, useBlockLayout } from 'react-table'
import { useNavigate } from 'react-router-dom'
import _ from 'lodash'
import { toast } from 'react-hot-toast'
import api from 'Src/api'
import { fetchBots } from 'Src/actions/bots'

import guidesLogo from 'Assets/images/empty-guides.svg?url'

import useOutsideClickListener from 'LegacyUtils/hooks/useOutsideClickListener'

import { getAllCampaigns, getSpecificCampaign, getAllBots } from 'Selectors/bots'

const Options = ({ id }) => {
  const [show, setShow] = useState(false)
  const ref = useRef()
  const dispatch = useDispatch()

  const closeHandler = useCallback(() => setShow(false), [setShow])
  const bots = useSelector(getAllBots)
  const campaign = useSelector((state) => getSpecificCampaign(state, id))

  useOutsideClickListener(ref, closeHandler, show)

  const deleteCampaign = useCallback(
    async (campaign) => {
      const UPDATE_BOT_URL = 'v1/bots/:botId'
      const bot = _.find(bots, { id: campaign.botId })
      const campaign_rules = bot.settings.campaign_rules.filter((rule) => rule.id !== campaign.id)
      const payload = {
        widget_type: bot.widget_type,
        status: bot.status,
        settings: {
          campaign_rules
        }
      }
      try {
        await api.patch(UPDATE_BOT_URL, payload, { urlParams: { botId: campaign.botId } })
        toast.success('Deleted campaign')
        dispatch(fetchBots())
      } catch (err) {
        toast.error('Error while deleting campaign')
      }
    },
    [bots, dispatch]
  )

  const onDeleteClick = useCallback(
    (e) => {
      e.preventDefault()
      const canDelete = confirm('Are you sure you want to delete this campaign? This cannot be undone.')
      if (!canDelete) return
      deleteCampaign(campaign)
    },
    [campaign, deleteCampaign]
  )

  const defaultClick = useCallback(
    (e) => {
      e.preventDefault()
      setShow(!show)
    },
    [show]
  )

  return (
    <div className='options-dots' onClick={defaultClick}>
      {show && (
        <div className='dropdown-menu' ref={ref}>
          <span className='dropdown-menu-arrow' />
          <a className='dropdown-item' onClick={onDeleteClick}>
            Delete
          </a>
        </div>
      )}
    </div>
  )
}

const CampaignsList = () => {
  const campaigns = useSelector(getAllCampaigns)
  const totalCampaignsCount = campaigns.length
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const columns = useMemo(() => {
    return [
      {
        Header: 'NAME',
        accessor: 'name'
      },
      {
        Header: 'BOT',
        accessor: 'botName'
      },
      {
        Header: 'STATUS',
        accessor: (row) => row,
        Cell: ({ cell: { value } }) => {
          const active = value.active
          return active ? 'Active' : 'Inactive'
        }
      }
    ]
  }, [])

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: campaigns,
      initialState: { pageIndex: 0, pageSize: 15 },
      manualPagination: true
    },
    usePagination,
    useBlockLayout
  )

  useEffect(() => {
    dispatch(fetchBots())
  }, [dispatch])

  return (
    <div className='guides-container'>
      <div className='page-top-bar'>
        <div className='title'>Campaigns</div>
        {rows.length > 0 && (
          <div className='right'>
            <button className='button-primary mr-3' onClick={() => navigate('create')}>
              New Campaign
            </button>
          </div>
        )}
      </div>
      {rows.length > 0 && (
        <div>
          <div className='list-container'>
            <div {...getTableProps()} className='table'>
              <div className='thead'>
                {headerGroups.map((headerGroup) => {
                  const { key } = headerGroup.getHeaderGroupProps()
                  return (
                    <div className='tr' key={key} {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => {
                        const { key } = column.getHeaderProps()
                        return (
                          <div className='th' {...column.getHeaderProps()} key={key}>
                            {column.render('Header')}
                          </div>
                        )
                      })}
                      <div className='th options-column' />
                    </div>
                  )
                })}
              </div>
              <div className='tbody' {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row)
                  const { key, ...rowProps } = row.getRowProps()
                  const id = row.original.id

                  return (
                    <div {...rowProps} key={key} className='pointer tr'>
                      {row.cells.map((cell) => {
                        const { key, ...cellProps } = cell.getCellProps()
                        return (
                          <div className='td' {...cellProps} key={key} onClick={() => navigate(`edit/${id}`)}>
                            {cell.render('Cell')}
                          </div>
                        )
                      })}
                      <div className='options td'>
                        <Options id={id} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      {totalCampaignsCount === 0 && (
        <div className='empty-state'>
          <div className='empty-state-content'>
            <img className='mb-3' src={guidesLogo} alt='' />
            <h1>Campaigns help with engaging your visitors</h1>
            <p>With campaigns, you can set up rules to show different targeted flows to different visitors</p>
            <button onClick={() => navigate('create')} className='button-primary mb-3'>
              Create a new Campaign
            </button>
            <br />
          </div>
        </div>
      )}
    </div>
  )
}

export default CampaignsList
